/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react';
import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';

import {CheckCircleIcon} from '@salesforce/retail-react-app/app/components/icons';
import {Flex, Stack, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';
import {validatePassword} from '@salesforce/retail-react-app/app/utils/password-utils';

/**
 * UI component for styling password requirement line
 */
const PasswordRequirement = ({isValid, children, styles}) => {
  const iconStyles = {
    color: isValid ? 'brandColors.green' : 'brandColors.greyscale40',
  };

  const textStyles = {
    color: isValid ? 'brandColors.greyscale90' : 'brandColors.greyscale60',
  };


  return (
    <Flex {...styles.item}>
      <CheckCircleIcon {...styles.icon} {...iconStyles} />
      <Text {...styles.text} {...textStyles}>
        {children}
      </Text>
    </Flex>
  );
};

PasswordRequirement.propTypes = {
  /** Should it render in valid state */
  isValid: PropTypes.bool,

  /** The requirement text */
  children: PropTypes.any,

  /** The styles */
  styles: PropTypes.object,
};

/**
 * Renders a list of password requirments. Each requirement line toggles to its `isValid`
 * state when the given password value meets the associated critieria.
 */
const PasswordRequirements = ({value}) => {
  const pwValidations = validatePassword(value);
  const styles = useMultiStyleConfig('PasswordRequirements', {});

  return (
    <Stack {...styles.container}>
      <PasswordRequirement isValid={pwValidations.hasMinChars} styles={styles}>
        <FormattedMessage
          defaultMessage="8 characters minimum"
          description="Password requirement"
          id="password_requirements.error.eight_letter_minimum"
        />
      </PasswordRequirement>
      <PasswordRequirement isValid={pwValidations.hasUppercase} styles={styles}>
        <FormattedMessage
          defaultMessage="1 uppercase letter"
          description="Password requirement"
          id="password_requirements.error.one_uppercase_letter"
        />
      </PasswordRequirement>
      <PasswordRequirement isValid={pwValidations.hasLowercase} styles={styles}>
        <FormattedMessage
          defaultMessage="1 lowercase letter"
          description="Password requirement"
          id="password_requirements.error.one_lowercase_letter"
        />
      </PasswordRequirement>
      <PasswordRequirement isValid={pwValidations.hasNumber} styles={styles}>
        <FormattedMessage
          defaultMessage="1 number"
          description="Password requirement"
          id="password_requirements.error.one_number"
        />
      </PasswordRequirement>
      <PasswordRequirement isValid={pwValidations.hasSpecialChar} styles={styles}>
        <FormattedMessage
          defaultMessage="1 special character (example: , S ! % #)"
          description="Password requirement"
          id="password_requirements.error.one_special_character"
        />
      </PasswordRequirement>
    </Stack>
  );
};

PasswordRequirements.propTypes = {
  /** The password to check against */
  value: PropTypes.string,
};

export default PasswordRequirements;
